import { Pill } from "@/components/ui/pill";
import { Select } from "@/components/ui/select";
import type { ImportCharacteristicColumn, ValueTranslation } from "@/types/data-import.types";

interface ValueTranslationTableProps {
  column: ImportCharacteristicColumn;
  options: Array<{ id: string; label: string }>;
  onChange: (translations: Record<string, ValueTranslation>) => void;
  translations: Record<string, ValueTranslation>;
}

const NAO_INFORMADO = "__NAO_INFORMADO__";

/**
 * Spec 084 (FR-019 · FR-021) — o DE-PARA das grafias que vieram no arquivo.
 *
 * As grafias aparecem EXATAMENTE como vieram, com a contagem de linhas: é isso que deixa o gestor
 * ver que "tipo a" e "TIPO-A" são a mesma coisa nesta planilha — e ele decide, não a plataforma.
 * Enquanto uma grafia não tiver resposta, a importação é recusada; por isso a pendência aparece em
 * TEXTO, e não só numa cor de fundo.
 */
export function ValueTranslationTable({
  column,
  options,
  onChange,
  translations,
}: ValueTranslationTableProps) {
  if (column.kind === "TEXT") {
    return (
      <p className="text-[12px] text-text-secondary">
        Característica de texto livre: cada célula entra como está, sem correspondência a definir.
        {column.emptyRows > 0
          ? ` ${column.emptyRows} linha(s) em branco entram como "não informado".`
          : ""}
      </p>
    );
  }

  function set(raw: string, value: string) {
    const next = { ...translations };
    if (value === "") delete next[raw];
    else if (value === NAO_INFORMADO) next[raw] = { notInformed: true };
    else next[raw] = { valueId: value };
    onChange(next);
  }

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] text-text-secondary">
          O que cada grafia do arquivo significa
        </span>
        {column.pendingCount > 0 ? (
          <Pill tone="warning" withDot>
            {column.pendingCount} sem resposta
          </Pill>
        ) : (
          <Pill tone="success" withDot>
            Todas respondidas
          </Pill>
        )}
        {column.emptyRows > 0 ? (
          <span className="text-[11px] text-text-tertiary">
            {column.emptyRows} linha(s) em branco entram como &quot;não informado&quot;.
          </span>
        ) : null}
      </div>
      <ul className="divide-y divide-border-default rounded-md border border-border-default">
        {column.values.map((v) => {
          const atual = translations[v.raw];
          const valor = !atual ? "" : "notInformed" in atual ? NAO_INFORMADO : atual.valueId;
          return (
            <li
              key={v.raw}
              className="grid items-center gap-2 px-2 py-1.5 md:grid-cols-[1fr_auto_1fr]"
            >
              <span className="min-w-0 truncate font-mono text-[12px] text-text-primary">
                {v.raw}
              </span>
              <span className="text-[11px] text-text-tertiary">
                {v.rows} {v.rows === 1 ? "linha" : "linhas"}
              </span>
              <Select
                aria-label={`O que "${v.raw}" significa`}
                value={valor}
                onChange={(e) => set(v.raw, e.target.value)}
              >
                <option value="">Sem resposta</option>
                {options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
                <option value={NAO_INFORMADO}>não informado</option>
              </Select>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
